import {
  resolveAgeReferenceDate,
  resolveMemberAge,
  validateMemberAgeSource,
} from "@/modules/add-new-data/lib/age";
import {
  applySkipPattern,
  getAddNewDataCodeErrors,
  getApplicabilityForQuestion,
  householdQuestionCodedFields,
  isExplicitSelectableNotApplicableCode,
  memberCodedFields,
  type MemberFieldName,
} from "@/modules/add-new-data/lib/cleansing";
import type { AddNewDataFormValues } from "@/modules/add-new-data/lib/schema";
import {
  deriveResidentTypeCode,
  parseLengthOfStayDuration,
} from "@/modules/add-new-data/lib/questionnaire-split-values";
import { cleanRBIMCode, isRBIMNotApplicable, rbimCodeLabel } from "@/lib/rbimCodeMap";
import { getHouseholdDeathAgeError } from "@/modules/add-new-data/lib/household-death-age-validation";
import { getDuplicateFamilyHeadIssues } from "@/modules/add-new-data/lib/family-head-validation";

export type ValidationNotice = {
  id: string;
  severity: "error" | "warning";
  message: string;
};

export type CurrentStepValidationError = {
  path: string;
  message: string;
};

type HouseholdRoleError = {
  id: string;
  severity: "error" | "warning";
  message: string;
  relationshipLabel: string;
  rows: number[];
};

export function getMemberDisplayName(member: AddNewDataFormValues["members"][number]) {
  return [member.firstName, member.middleName, member.lastName].filter(Boolean).join(" ") || "Member";
}

const maleRelationshipCodes = ["03", "05", "09", "11", "13", "15", "17"];
const femaleRelationshipCodes = ["04", "06", "10", "12", "14", "16", "18"];

const currentStepMemberFields: Record<number, readonly MemberFieldName[]> = {
  1: [
    "firstName",
    "middleName",
    "lastName",
    "relationshipToHead",
    "sex",
    "dateOfBirth",
    "placeOfBirth",
    "nationality",
    "nationalityOther",
    "civilStatus",
    "religion",
    "ethnicity",
    "highestEducation",
    "currentlyEnrolled",
    "schoolLevel",
    "placeOfSchool",
    "reasonNotAttendingSchool",
  ],
  2: [
    "monthlyIncome",
    "sourceOfIncome",
    "sourceOfIncomeOther",
    "workStatus",
    "placeOfWork",
    "placeOfDelivery",
    "placeOfDeliveryOther",
    "birthAttendant",
    "birthAttendantOther",
    "immunization",
    "livingChildren",
    "livingChildrenAlive",
    "familyPlanningUse",
    "familyPlanningUseOther",
    "fpMethodSource",
    "fpMethodSourceOther",
    "intentionToUseFp",
    "intentionToUseFpDetail",
  ],
  3: [
    "healthInsurance",
    "healthInsuranceOther",
    "facilityVisitedPast12Months",
    "facilityVisitedPast12MonthsOther",
    "healthFacilityVisitReason",
    "healthFacilityVisitReasonOther",
    "disability",
    "soloParentStatus",
    "registeredSeniorCitizen",
    "registeredVoterBarangay",
    "lengthOfStayInBarangay",
    "previousResidenceFiveYears",
    "previousResidenceSixMonths",
    "residentType",
  ],
  4: [
    "dateOfTransfer",
    "reasonLeavingPreviousResidence1",
    "reasonLeavingPreviousResidence1Other",
    "reasonLeavingPreviousResidence2",
    "reasonLeavingPreviousResidence2Other",
    "reasonLeavingPreviousResidence3",
    "reasonLeavingPreviousResidence3Other",
    "returnToPreviousResidence",
    "returnToPreviousResidenceDetail",
    "transferReason1",
    "transferReason1Other",
    "transferReason2",
    "transferReason2Other",
    "transferReason3",
    "transferReason3Other",
    "durationOfStayCurrentBarangay",
    "durationOfStayCurrentBarangayDetail",
    "hasValidCtc",
    "ctcIssuedInBarangay",
    "skillsTrainingInterest",
    "skills",
    "skillsOther",
  ],
};

const householdRequiredFields: Array<[keyof AddNewDataFormValues["household"], string]> = [
  ["province", "Province"],
  ["municipality", "City/Municipality"],
  ["barangay", "Barangay"],
  ["address", "Address"],
  ["householdCode", "Household Code"],
  ["family", "Family"],
  ["respondentName", "Name of Respondent"],
  ["householdHead", "Household Head"],
  ["totalMembers", "Total No. of Household Members"],
];

export function validateHouseholdRoles(members: AddNewDataFormValues["members"]): HouseholdRoleError[] {
  const headRows = getRowsByRelationshipCode(members, "01");
  const spouseRows = getRowsByRelationshipCode(members, "02");
  const errors: HouseholdRoleError[] = [];

  if (headRows.length > 1) {
    errors.push({
      id: "multiple-household-heads",
      severity: "error",
      message: "Only one household head is allowed.",
      relationshipLabel: "Head",
      rows: headRows,
    });
  }

  if (spouseRows.length > 1) {
    errors.push({
      id: "multiple-household-spouses",
      severity: "error",
      message: "Only one spouse is allowed for the household head.",
      relationshipLabel: "Spouse",
      rows: spouseRows,
    });
  }

  return errors;
}

export function validateCurrentStep(
  stepId: number,
  values: AddNewDataFormValues,
): CurrentStepValidationError[] {
  if (stepId === 0) {
    return validateFrontPageStep(values);
  }

  if (stepId === 5) {
    return validateHouseholdQuestionsStep(values);
  }

  const memberFields = currentStepMemberFields[stepId];

  if (!memberFields) {
    return [];
  }

  const normalizedValues: AddNewDataFormValues = {
    ...values,
    members: values.members.map((member) => applySkipPattern(member)),
  };
  const ageReferenceDate = resolveAgeReferenceDate(normalizedValues.interviews, {
    members: normalizedValues.members,
  });

  const errors: CurrentStepValidationError[] = [];

  normalizedValues.members.forEach((member, index) => {
    validateRequiredMemberFields(stepId, member, index, errors, ageReferenceDate);
    validateMemberAge(member, index, memberFields, errors, ageReferenceDate);
    validateAnsweredCodesOnCurrentStep(member, index, memberFields, errors);
    validateSkipConflictsOnCurrentStep(member, index, memberFields, errors);
    validateCurrentStepDatePatterns(member, index, memberFields, errors);
  });

  if (stepId === 1) {
    if (
      normalizedValues.household.family !== "A" &&
      !normalizedValues.household.familyRelationshipToHouseholdHead?.trim()
    ) {
      errors.push({
        path: "household.familyRelationshipToHouseholdHead",
        message: `Relationship of Family ${normalizedValues.household.family} to the Household Head is required.`,
      });
    }

    validateHouseholdRoles(normalizedValues.members).forEach((error) => {
      error.rows.forEach((row) => {
        errors.push({
          path: `members.${row - 1}.relationshipToHead`,
          message: error.message,
        });
      });
    });

    if (!hasHouseholdHead(normalizedValues.members)) {
      errors.push({
        path: "members.0.relationshipToHead",
        message: "One household head is required.",
      });
    }

    getDuplicateFamilyHeadIssues(normalizedValues.members).forEach((issue) => {
      issue.memberIndexes.forEach((memberIndex) => {
        errors.push({
          path: `members.${memberIndex}.isFamilyHead`,
          message: issue.message,
        });
      });
    });
  }

  return dedupeStepErrors(errors);
}

export function checkRelationshipSexWarning(
  relationshipToHead: string | null | undefined,
  sex: string | null | undefined,
) {
  const relationship = cleanRBIMCode("q02_relationship_to_head", relationshipToHead);
  const sexResult = cleanRBIMCode("q03_sex", sex);

  if (!relationship.code || !sexResult.code || relationship.error || sexResult.error) {
    return null;
  }

  if (maleRelationshipCodes.includes(relationship.code) && sexResult.code === "2") {
    return {
      expectedSex: "Male" as const,
      message: `Relationship "${relationship.label}" usually corresponds to sex "Male".`,
      relationshipLabel: relationship.label ?? relationship.code,
      sexLabel: sexResult.label ?? sexResult.code,
    };
  }

  if (femaleRelationshipCodes.includes(relationship.code) && sexResult.code === "1") {
    return {
      expectedSex: "Female" as const,
      message: `Relationship "${relationship.label}" usually corresponds to sex "Female".`,
      relationshipLabel: relationship.label ?? relationship.code,
      sexLabel: sexResult.label ?? sexResult.code,
    };
  }

  return null;
}

function validateFrontPageStep(values: AddNewDataFormValues): CurrentStepValidationError[] {
  return householdRequiredFields.flatMap(([field, label]) => {
    if (String(values.household[field] ?? "").trim()) {
      return [];
    }

    return [{
      path: `household.${field}`,
      message: `${label} is required before proceeding.`,
    }];
  });
}

function validateHouseholdQuestionsStep(values: AddNewDataFormValues): CurrentStepValidationError[] {
  const errors: CurrentStepValidationError[] = [];

  const q46To57InheritedFromFamilyA =
    /^[B-J]$/i.test(
      String(
        values.household.family ?? "",
      ).trim(),
    );

  const q46To57Fields = new Set([
    "housingTenure",
    "lotTenure",
    "hazardProneArea",
    "disasterExperiencedPastFiveYears",
    "knowsNearestEvacuationCenter",
    "lightingFuel",
    "cookingFuel",
    "drinkingWaterSource",
    "garbageDisposal",
    "segregatesGarbage",
    "toiletFacility",
    "buildingType",
    "wallMaterial",
  ]);

  householdQuestionCodedFields.forEach(({ field, questionKey }) => {
    if (
      q46To57InheritedFromFamilyA &&
      q46To57Fields.has(field)
    ) {
      return;
    }

    const value = values.householdQuestions[field];

    if (!value?.trim()) {
      return;
    }

    const result = cleanRBIMCode(questionKey, value);

    if (result.error) {
      errors.push({
        path: `householdQuestions.${field}`,
        message: result.error,
      });
    }
  });

  const q58AgeError = getHouseholdDeathAgeError(
    "q58",
    values.householdQuestions.femaleDeathPast12Months,
    values.householdQuestions.femaleDeathAge,
  );
  const q59AgeError = getHouseholdDeathAgeError(
    "q59",
    values.householdQuestions.childDeathPast12Months,
    values.householdQuestions.childDeathAge,
  );

  if (q58AgeError) {
    errors.push({
      path: "householdQuestions.femaleDeathAge",
      message: q58AgeError,
    });
  }

  if (q59AgeError) {
    errors.push({
      path: "householdQuestions.childDeathAge",
      message: q59AgeError,
    });
  }

  return errors;
}

function validateMemberAge(
  member: AddNewDataFormValues["members"][number],
  memberIndex: number,
  memberFields: readonly MemberFieldName[],
  errors: CurrentStepValidationError[],
  ageReferenceDate: Date,
) {
  if (!memberFields.includes("dateOfBirth")) {
    return;
  }

  const result = validateMemberAgeSource(member, ageReferenceDate);

  if (result.isValid) {
    return;
  }

  errors.push({
    path: `members.${memberIndex}.${result.field ?? "dateOfBirth"}`,
    message: result.message ?? "Enter the Date of Birth or select Date of Birth Unknown.",
  });
}

function validateRequiredMemberFields(
  stepId: number,
  member: AddNewDataFormValues["members"][number],
  memberIndex: number,
  errors: CurrentStepValidationError[],
  ageReferenceDate: Date,
) {
  if (stepId === 1) {
    addRequiredMemberField(member, memberIndex, "lastName", "Q1 Surname", errors, true);
    addRequiredMemberField(member, memberIndex, "firstName", "Q1 First Name", errors, true);
    addRequiredMemberField(member, memberIndex, "relationshipToHead", "Q2 Relationship to Household Head", errors, true);
    addRequiredMemberField(member, memberIndex, "sex", "Q3 Sex", errors, true);
    addRequiredMemberField(member, memberIndex, "placeOfBirth", "Q6 Place of Birth", errors, true);
    addRequiredMemberField(member, memberIndex, "nationality", "Q7 Nationality", errors, true);
    addNonFilipinoDetailIssue(member, memberIndex, errors);

    if (!validateMemberAgeSource(member, ageReferenceDate).isValid) {
      return;
    }

    addRequiredMemberField(member, memberIndex, "highestEducation", "Q11 Highest Level of Education", errors);
    addRequiredMemberField(member, memberIndex, "currentlyEnrolled", "Q12 Currently Enrolled", errors);

    const enrollment = cleanRBIMCode("q12_currently_enrolled", member.currentlyEnrolled);
    if (!enrollment.error && (enrollment.code === "1" || enrollment.code === "2")) {
      addRequiredMemberField(member, memberIndex, "schoolLevel", "Q13 School Level", errors);
      addRequiredMemberField(member, memberIndex, "placeOfSchool", "Q14 Place of School", errors);
    }

    if (getApplicabilityForQuestion(member, "reasonNotAttendingSchool") === "applicable") {
      addRequiredMemberField(member, memberIndex, "reasonNotAttendingSchool", "Q15 Reason for Not Attending School", errors);

      const reason = cleanRBIMCode("q15_reason_not_attending_school", member.reasonNotAttendingSchool);
      if (
        !reason.error &&
        reason.code === "20" &&
        !hasOtherSpecify(member.reasonNotAttendingSchool, "20") &&
        !member.reasonNotAttendingSchoolOther?.trim()
      ) {
        errors.push({
          path: `members.${memberIndex}.reasonNotAttendingSchoolOther`,
          message: "Please specify the reason for not attending school.",
        });
      }
    }

  }

  if (stepId === 2) {
    validateEconomicHealthStep(member, memberIndex, errors);
  }

  if (stepId === 3) {
    validateHealthMigrationStep(member, memberIndex, errors);
  }

  if (stepId === 4) {
    validateMigrationSkillsStep(member, memberIndex, errors);
  }
}

function validateEconomicHealthStep(
  member: AddNewDataFormValues["members"][number],
  memberIndex: number,
  errors: CurrentStepValidationError[],
) {
  if (getApplicabilityForQuestion(member, "monthlyIncome") === "applicable") {
    addRequiredMemberField(member, memberIndex, "monthlyIncome", "Q16 Monthly Income", errors);
    addMonthlyIncomeIssue(member, memberIndex, errors);
  }

  const monthlyIncome = parseNumericAmount(member.monthlyIncome, { allowNone: true });
  const hasPositiveIncome = monthlyIncome.valid && monthlyIncome.value !== null && monthlyIncome.value > 0;
  const sourceOfIncome = cleanRBIMCode("q17_source_of_income", member.sourceOfIncome);

  if (hasPositiveIncome) {
    addRequiredMemberField(member, memberIndex, "sourceOfIncome", "Q17 Source of Income", errors);
  }

  if (
    !sourceOfIncome.error &&
    ["1", "2", "3", "4", "5"].includes(sourceOfIncome.code)
  ) {
    addRequiredMemberField(
      member,
      memberIndex,
      "sourceOfIncomeOther",
      "Q17 Source of Income - Specify",
      errors,
    );
  }

  if (!sourceOfIncome.error && (sourceOfIncome.code === "1" || sourceOfIncome.code === "2")) {
    addRequiredMemberField(member, memberIndex, "workStatus", "Q18 Status of Work/Business", errors);
  }

  if (hasPositiveIncome && !sourceOfIncome.error && ["1", "2"].includes(sourceOfIncome.code)) {
    addRequiredMemberField(member, memberIndex, "placeOfWork", "Q19 Place of Work/Business", errors);
  }

  if (getApplicabilityForQuestion(member, "placeOfDelivery") === "applicable") {
    addRequiredMemberField(member, memberIndex, "placeOfDelivery", "Q20 Place of Delivery", errors);
    addDetailIssue(
      member,
      memberIndex,
      "placeOfDeliveryOther",
      "placeOfDelivery",
      "5",
      "Please specify the place of delivery.",
      errors,
    );
  }

  if (getApplicabilityForQuestion(member, "birthAttendant") === "applicable") {
    addRequiredMemberField(member, memberIndex, "birthAttendant", "Q21 Birth Attendant", errors);
    addDetailIssue(
      member,
      memberIndex,
      "birthAttendantOther",
      "birthAttendant",
      "5",
      "Please specify the person who assisted in the delivery.",
      errors,
    );
  }

  addRequiredMemberField(member, memberIndex, "immunization", "Q22 Immunization", errors);
  validateLivingChildren(member, memberIndex, errors);

  if (getApplicabilityForQuestion(member, "familyPlanningUse") === "applicable") {
    addRequiredMemberField(member, memberIndex, "familyPlanningUse", "Q24 Family Planning Method", errors);
  }

  const familyPlanningUse = cleanRBIMCode("q24_family_planning_use", member.familyPlanningUse);
  if (
    getApplicabilityForQuestion(member, "fpMethodSource") === "applicable" &&
    !familyPlanningUse.error &&
    familyPlanningUse.code
  ) {
    addRequiredMemberField(member, memberIndex, "fpMethodSource", "Q25 Source of FP Method", errors);
    addDetailIssue(
      member,
      memberIndex,
      "fpMethodSourceOther",
      "fpMethodSource",
      "6",
      "Please specify the source of FP method.",
      errors,
    );
  }

  if (getApplicabilityForQuestion(member, "intentionToUseFp") === "applicable") {
    addRequiredMemberField(member, memberIndex, "intentionToUseFp", "Q26 Intention to Use FP", errors);

    const intention = cleanRBIMCode("q26_intention_to_use_fp", member.intentionToUseFp);
    if (!intention.error && (intention.code === "1" || intention.code === "2")) {
      addFpIntentionDetailIssue(member, memberIndex, intention.code, errors);
    }
  }
}

function validateHealthMigrationStep(
  member: AddNewDataFormValues["members"][number],
  memberIndex: number,
  errors: CurrentStepValidationError[],
) {
  addDetailIssue(
    member,
    memberIndex,
    "healthInsuranceOther",
    "healthInsurance",
    "8",
    "Please specify the health insurance.",
    errors,
  );

  addDetailIssue(
    member,
    memberIndex,
    "facilityVisitedPast12MonthsOther",
    "facilityVisitedPast12Months",
    "8",
    "Please specify the health facility visited.",
    errors,
  );

  addDetailIssue(
    member,
    memberIndex,
    "healthFacilityVisitReasonOther",
    "healthFacilityVisitReason",
    "8",
    "Please specify the reason for visit.",
    errors,
  );

  addLengthOfStayIssue(member, memberIndex, errors);
  addRequiredMemberField(member, memberIndex, "residentType", "Q37 Type of Resident", errors);
  addResidentTypeConsistencyIssue(member, memberIndex, errors);
}

function validateMigrationSkillsStep(
  member: AddNewDataFormValues["members"][number],
  memberIndex: number,
  errors: CurrentStepValidationError[],
) {
  addRequiredMemberField(member, memberIndex, "dateOfTransfer", "Q38 Date of Transfer", errors);
  addRequiredMemberField(member, memberIndex, "reasonLeavingPreviousResidence1", "Q39A Reason for Leaving Previous Residence", errors);
  addOtherReasonIssue(
    member,
    memberIndex,
    "reasonLeavingPreviousResidence1",
    "reasonLeavingPreviousResidence1Other",
    "15",
    "Please specify the reason for leaving.",
    errors,
  );
  addOtherReasonIssue(
    member,
    memberIndex,
    "reasonLeavingPreviousResidence2",
    "reasonLeavingPreviousResidence2Other",
    "15",
    "Please specify the reason for leaving.",
    errors,
  );
  addOtherReasonIssue(
    member,
    memberIndex,
    "reasonLeavingPreviousResidence3",
    "reasonLeavingPreviousResidence3Other",
    "15",
    "Please specify the reason for leaving.",
    errors,
  );

  addRequiredMemberField(member, memberIndex, "returnToPreviousResidence", "Q40 Return to Previous Residence", errors);
  addYesDetailIssue(
    member,
    memberIndex,
    "returnToPreviousResidence",
    "returnToPreviousResidenceDetail",
    "Please provide the return year.",
    errors,
  );

  addRequiredMemberField(member, memberIndex, "transferReason1", "Q41A Reason for Transferring to Barangay", errors);
  addOtherReasonIssue(
    member,
    memberIndex,
    "transferReason1",
    "transferReason1Other",
    "5",
    "Please specify the reason for transferring.",
    errors,
  );
  addOtherReasonIssue(
    member,
    memberIndex,
    "transferReason2",
    "transferReason2Other",
    "5",
    "Please specify the reason for transferring.",
    errors,
  );
  addOtherReasonIssue(
    member,
    memberIndex,
    "transferReason3",
    "transferReason3Other",
    "5",
    "Please specify the reason for transferring.",
    errors,
  );

  addRequiredMemberField(member, memberIndex, "durationOfStayCurrentBarangay", "Q42 Duration of Stay in Current Barangay", errors);
  addYesDetailIssue(
    member,
    memberIndex,
    "durationOfStayCurrentBarangay",
    "durationOfStayCurrentBarangayDetail",
    "Please provide the required details.",
    errors,
  );

  addCtcIssues(member, memberIndex, errors);
  addRequiredMemberField(member, memberIndex, "skills", "Q45 Skills", errors);
  addDetailIssue(
    member,
    memberIndex,
    "skillsOther",
    "skills",
    "18",
    "Please specify the skill.",
    errors,
  );
}

function addRequiredMemberField(
  member: AddNewDataFormValues["members"][number],
  memberIndex: number,
  field: MemberFieldName,
  label: string,
  errors: CurrentStepValidationError[],
  ignoreApplicability = false,
) {
  if (!ignoreApplicability && getApplicabilityForQuestion(member, field) !== "applicable") {
    return;
  }

  const value = member[field];

  if (!isRequiredFieldMissing(field, value)) {
    return;
  }

  errors.push({
    path: `members.${memberIndex}.${field}`,
    message: getRequiredFieldMessage(field, label),
  });
}

function addNonFilipinoDetailIssue(
  member: AddNewDataFormValues["members"][number],
  memberIndex: number,
  errors: CurrentStepValidationError[],
) {
  const nationality = cleanRBIMCode("q07_nationality", member.nationality);

  if (nationality.error || nationality.code !== "2") {
    return;
  }

  if (/^0*2(?:\s*[-,:]\s*|\s+).+/i.test(String(member.nationality ?? "").trim())) {
    return;
  }

  if (member.nationalityOther?.trim()) {
    return;
  }

  errors.push({
    path: `members.${memberIndex}.nationalityOther`,
    message: "Please specify the nationality.",
  });
}

function addMonthlyIncomeIssue(
  member: AddNewDataFormValues["members"][number],
  memberIndex: number,
  errors: CurrentStepValidationError[],
) {
  const parsedIncome = parseNumericAmount(member.monthlyIncome, { allowNone: true });

  if (parsedIncome.blank || parsedIncome.valid) {
    return;
  }

  errors.push({
    path: `members.${memberIndex}.monthlyIncome`,
    message: parsedIncome.isNegative ? "Q16 Monthly Income cannot be negative." : "Q16 Monthly Income must be numeric or 0.",
  });
}

function validateLivingChildren(
  member: AddNewDataFormValues["members"][number],
  memberIndex: number,
  errors: CurrentStepValidationError[],
) {
  if (getApplicabilityForQuestion(member, "livingChildren") !== "applicable") {
    return;
  }

  addRequiredMemberField(member, memberIndex, "livingChildren", "Q23 Total Pregnancies", errors);

  const totalPregnancies = parseWholeNumber(member.livingChildren);
  if (totalPregnancies.blank) {
    return;
  }

  if (!totalPregnancies.valid) {
    errors.push({
      path: `members.${memberIndex}.livingChildren`,
      message: totalPregnancies.isNegative
        ? "Q23 Total Pregnancies cannot be negative."
        : "Q23 Total Pregnancies must be numeric.",
    });
    return;
  }

  const totalPregnanciesValue = Number(totalPregnancies.value);

  if (totalPregnanciesValue === 0) {
    if (member.livingChildrenAlive?.trim() && !isRBIMNotApplicable(member.livingChildrenAlive)) {
      errors.push({
        path: `members.${memberIndex}.livingChildrenAlive`,
        message: "Q23 Living Children must be 99 - N/A when total pregnancies is 0.",
      });
    }
    return;
  }

  addRequiredMemberField(member, memberIndex, "livingChildrenAlive", "Q23 Living Children", errors);

  const livingChildren = parseWholeNumber(member.livingChildrenAlive);
  if (livingChildren.blank) {
    return;
  }

  if (!livingChildren.valid) {
    errors.push({
      path: `members.${memberIndex}.livingChildrenAlive`,
      message: livingChildren.isNegative
        ? "Q23 Living Children cannot be negative."
        : "Q23 Living Children must be numeric.",
    });
    return;
  }

  const livingChildrenValue = Number(livingChildren.value);

  if (livingChildrenValue > totalPregnanciesValue) {
    errors.push({
      path: `members.${memberIndex}.livingChildrenAlive`,
      message: "Q23 Living Children cannot be greater than total pregnancies.",
    });
  }
}

function addDetailIssue(
  member: AddNewDataFormValues["members"][number],
  memberIndex: number,
  detailField: MemberFieldName,
  codedField: MemberFieldName,
  code: string,
  message: string,
  errors: CurrentStepValidationError[],
) {
  const codedValue = member[codedField];
  const detailValue = member[detailField];
  const questionKey = getMemberFieldQuestionKey(codedField);

  if (!questionKey || getApplicabilityForQuestion(member, codedField) !== "applicable") {
    return;
  }

  const codedResult = cleanRBIMCode(questionKey, codedValue);

  if (codedResult.error || codedResult.code !== code) {
    return;
  }

  if (hasOtherSpecify(codedValue, code) || detailValue?.trim()) {
    return;
  }

  errors.push({
    path: `members.${memberIndex}.${detailField}`,
    message,
  });
}

function addFpIntentionDetailIssue(
  member: AddNewDataFormValues["members"][number],
  memberIndex: number,
  intentionCode: "1" | "2",
  errors: CurrentStepValidationError[],
) {
  const detailValue = String(member.intentionToUseFpDetail ?? "").trim();

  if (hasOtherSpecify(member.intentionToUseFp, intentionCode)) {
    return;
  }

  if (!detailValue || isRBIMNotApplicable(detailValue)) {
    errors.push({
      path: `members.${memberIndex}.intentionToUseFpDetail`,
      message:
        intentionCode === "1"
          ? "Please specify the intended family planning method."
          : "Please specify the reason for not intending to use FP.",
    });
    return;
  }

  if (intentionCode === "2") {
    return;
  }

  const method = cleanRBIMCode("q26_intended_fp_method", detailValue);

  if (method.error) {
    errors.push({
      path: `members.${memberIndex}.intentionToUseFpDetail`,
      message: "Please enter a valid FP method or select from the dropdown.",
    });
  }
}

function getMemberFieldQuestionKey(field: MemberFieldName) {
  return memberCodedFields.find((codedField) => codedField.field === field)?.questionKey;
}

function addOtherReasonIssue(
  member: AddNewDataFormValues["members"][number],
  memberIndex: number,
  codedField: MemberFieldName,
  detailField: MemberFieldName,
  code: string,
  message: string,
  errors: CurrentStepValidationError[],
) {
  if (getApplicabilityForQuestion(member, codedField) !== "applicable") {
    return;
  }

  addDetailIssue(member, memberIndex, detailField, codedField, code, message, errors);
}

function addYesDetailIssue(
  member: AddNewDataFormValues["members"][number],
  memberIndex: number,
  codedField: MemberFieldName,
  detailField: MemberFieldName,
  message: string,
  errors: CurrentStepValidationError[],
) {
  if (getApplicabilityForQuestion(member, codedField) !== "applicable") {
    return;
  }

  const result = cleanRBIMCode(
    codedField === "returnToPreviousResidence"
      ? "q40_return_to_previous_residence"
      : "q42_duration_of_stay_current_barangay",
    member[codedField],
  );

  if (result.error || result.code !== "1" || hasOtherSpecify(member[codedField], "1") || member[detailField]?.trim()) {
    return;
  }

  errors.push({
    path: `members.${memberIndex}.${detailField}`,
    message,
  });
}

function addLengthOfStayIssue(
  member: AddNewDataFormValues["members"][number],
  memberIndex: number,
  errors: CurrentStepValidationError[],
) {
  if (
    getApplicabilityForQuestion(member, "lengthOfStayInBarangay") !== "applicable" ||
    !member.lengthOfStayInBarangay?.trim()
  ) {
    return;
  }

  const duration = parseLengthOfStayDuration(member.lengthOfStayInBarangay);

  if (duration.valid) {
    return;
  }

  errors.push({
    path: `members.${memberIndex}.lengthOfStayInBarangay`,
    message: duration.message,
  });
}

function addCtcIssues(
  member: AddNewDataFormValues["members"][number],
  memberIndex: number,
  errors: CurrentStepValidationError[],
) {
  if (
    getApplicabilityForQuestion(
      member,
      "hasValidCtc",
    ) === "applicable"
  ) {
    const q43A =
      cleanRBIMCode(
        "q43a_has_valid_ctc",
        member.hasValidCtc,
      );

    if (
      q43A.error ||
      !["1", "2", "99"].includes(q43A.code)
    ) {
      errors.push({
        path:
          `members.${memberIndex}.hasValidCtc`,
        message:
          "Q43A requires Yes, No, or 99 - N/A before proceeding.",
      });
    }
  }

  if (
    getApplicabilityForQuestion(
      member,
      "ctcIssuedInBarangay",
    ) === "applicable"
  ) {
    const q43B =
      cleanRBIMCode(
        "q43b_ctc_issued_in_barangay",
        member.ctcIssuedInBarangay,
      );

    if (
      q43B.error ||
      !["1", "2", "99"].includes(q43B.code)
    ) {
      errors.push({
        path:
          `members.${memberIndex}.ctcIssuedInBarangay`,
        message:
          "Q43B requires Yes, No, or 99 - N/A before proceeding.",
      });
    }
  }
}

function addResidentTypeConsistencyIssue(
  member: AddNewDataFormValues["members"][number],
  memberIndex: number,
  errors: CurrentStepValidationError[],
) {
  const expectedCode = deriveResidentTypeCode(member.lengthOfStayInBarangay);

  if (!expectedCode) {
    return;
  }

  const residentType = cleanRBIMCode("q37_resident_type", member.residentType);

  if (!residentType.error && residentType.code === expectedCode) {
    return;
  }

  errors.push({
    path: `members.${memberIndex}.residentType`,
    message: "Q37 Type of Resident must match the Q34 length of stay.",
  });
}

function validateAnsweredCodesOnCurrentStep(
  member: AddNewDataFormValues["members"][number],
  memberIndex: number,
  memberFields: readonly MemberFieldName[],
  errors: CurrentStepValidationError[],
) {
  memberCodedFields.forEach(({ field, questionKey }) => {
    if (!memberFields.includes(field)) {
      return;
    }

    const value = member[field];
    if (!value?.trim() || getApplicabilityForQuestion(member, field) !== "applicable") {
      return;
    }

    const result = cleanRBIMCode(questionKey, value);
    if (!result.error) {
      return;
    }

    errors.push({
      path: `members.${memberIndex}.${field}`,
      message: getCodeErrorMessage(field, result.error),
    });
  });
}

function validateSkipConflictsOnCurrentStep(
  member: AddNewDataFormValues["members"][number],
  memberIndex: number,
  memberFields: readonly MemberFieldName[],
  errors: CurrentStepValidationError[],
) {
  void member;
  void memberIndex;
  void memberFields;
  void errors;
}

function validateQ40ReturnYear(
  member: AddNewDataFormValues["members"][number],
  memberIndex: number,
  errors: CurrentStepValidationError[],
) {
  if (
    getApplicabilityForQuestion(
      member,
      "returnToPreviousResidence",
    ) !== "applicable"
  ) {
    return;
  }

  const q40 = cleanRBIMCode(
    "q40_return_to_previous_residence",
    member.returnToPreviousResidence,
  );

  if (q40.error || q40.code !== "1") {
    return;
  }

  const returnYear = String(
    member.returnToPreviousResidenceDetail ?? "",
  ).trim();

  // Blank is handled by the existing Yes-detail required rule.
  if (!returnYear) {
    return;
  }

  if (!/^\d{4}$/.test(returnYear)) {
    errors.push({
      path: `members.${memberIndex}.returnToPreviousResidenceDetail`,
      message: "Q40 Return year must use YYYY format.",
    });
  }
}
function validateCurrentStepDatePatterns(
  member: AddNewDataFormValues["members"][number],
  memberIndex: number,
  memberFields: readonly MemberFieldName[],
  errors: CurrentStepValidationError[],
) {
  if (
    !memberFields.includes("dateOfTransfer") &&
    !memberFields.includes("returnToPreviousResidenceDetail") &&
    !memberFields.includes("durationOfStayCurrentBarangayDetail")
  ) {
    return;
  }

  if (
    memberFields.includes("dateOfTransfer") &&
    getApplicabilityForQuestion(member, "dateOfTransfer") === "applicable" &&
    isMeaningfulSkippedAnswer(member.dateOfTransfer) &&
    !isValidMonthYear(member.dateOfTransfer)
  ) {
    errors.push({
      path: `members.${memberIndex}.dateOfTransfer`,
      message: "Q38 Date of Transfer must use a valid MM/YYYY format.",
    });
  }

  validateQ40ReturnYear(member, memberIndex, errors);

}

function isRequiredFieldMissing(field: MemberFieldName, value: string | undefined) {
  const normalizedValue = String(value ?? "").trim();

  // Q33 registered-voter combination validation.
  if (field === "registeredVoterBarangay") {
    if (
      /^99\s*-\s*N\/A$/i.test(normalizedValue) ||
      /^2\s*-\s*No\s*\|\s*99\s*-\s*N\/A$/i.test(normalizedValue)
    ) {
      return false;
    }

    const yesMatch =
      /^1\s*-\s*Yes(?:\s*\|\s*(.*))?$/i.exec(normalizedValue);

    if (yesMatch) {
      const barangay = String(yesMatch[1] ?? "").trim();

      return (
        !barangay ||
        /^99\s*-\s*N\/A$/i.test(barangay)
      );
    }

    if (
      /^1\s*-\s*Yes\b/i.test(normalizedValue) ||
      /^2\s*-\s*No\b/i.test(normalizedValue)
    ) {
      return true;
    }

    // Preserve valid legacy records that stored only the barangay name.
    return (
      !normalizedValue ||
      isRBIMNotApplicable(normalizedValue)
    );
  }

  if (isExplicitSelectableNotApplicableCode(field, normalizedValue)) {
    return false;
  }

  return !normalizedValue || isRBIMNotApplicable(normalizedValue);
}

function parseNumericAmount(value: string | undefined, options: { allowNone?: boolean } = {}) {
  const normalizedValue = String(value ?? "").replace(/,/g, "").trim();

  if (!normalizedValue) {
    return { blank: true, valid: false, value: null, isNegative: false };
  }

  if (options.allowNone && /^(NONE|NO INCOME|N\/A|NA)$/i.test(normalizedValue)) {
    return { blank: false, valid: true, value: 0, isNegative: false };
  }

  if (!/^-?\d+(?:\.\d+)?$/.test(normalizedValue)) {
    return { blank: false, valid: false, value: null, isNegative: normalizedValue.startsWith("-") };
  }

  const numericValue = Number(normalizedValue);

  if (!Number.isFinite(numericValue) || numericValue < 0) {
    return { blank: false, valid: false, value: numericValue, isNegative: numericValue < 0 };
  }

  return { blank: false, valid: true, value: numericValue, isNegative: false };
}

function parseWholeNumber(value: string | undefined) {
  const normalizedValue = String(value ?? "").replace(/,/g, "").trim();

  if (!normalizedValue || isRBIMNotApplicable(normalizedValue)) {
    return { blank: true, valid: false, value: null, isNegative: false };
  }

  if (!/^-?\d+$/.test(normalizedValue)) {
    return { blank: false, valid: false, value: null, isNegative: normalizedValue.startsWith("-") };
  }

  const numericValue = Number(normalizedValue);

  if (!Number.isSafeInteger(numericValue) || numericValue < 0) {
    return { blank: false, valid: false, value: numericValue, isNegative: numericValue < 0 };
  }

  return { blank: false, valid: true, value: numericValue, isNegative: false };
}

function validateOptionalMonthYearDetail(
  member: AddNewDataFormValues["members"][number],
  memberIndex: number,
  codedField: MemberFieldName,
  detailField: MemberFieldName,
  label: string,
  errors: CurrentStepValidationError[],
  options: MonthYearValidationOptions & { allowDescriptiveText?: boolean } = {},
) {
  if (getApplicabilityForQuestion(member, codedField) !== "applicable") {
    return;
  }

  const value = member[detailField];

  if (!value?.trim() || isRBIMNotApplicable(value)) {
    return;
  }

  if (options.allowDescriptiveText && !/^\d+\s*\/\s*\d+$/.test(value.trim())) {
    return;
  }

  if (isValidMonthYear(value, options)) {
    return;
  }

  errors.push({
    path: `members.${memberIndex}.${detailField}`,
    message: options.allowFuture
      ? `${label} must use a valid MM/YYYY format.`
      : `${label} must use a valid MM/YYYY format and cannot be in the future.`,
  });
}

function hasHouseholdHead(members: AddNewDataFormValues["members"]) {
  return members.some((member) => {
    const result = cleanRBIMCode("q02_relationship_to_head", member.relationshipToHead);

    return !result.error && result.code === "01";
  });
}

function dedupeStepErrors(errors: CurrentStepValidationError[]) {
  const seen = new Set<string>();

  return errors.filter((error) => {
    const key = `${error.path}:${error.message}`;

    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
}

export function getSuspiciousEntryNotices(values: AddNewDataFormValues): ValidationNotice[] {
  const notices: ValidationNotice[] = [];
  const ageReferenceDate = resolveAgeReferenceDate(values.interviews, { members: values.members });

  validateHouseholdRoles(values.members).forEach((error) => {
    const rowList = formatRows(error.rows);

    notices.push({
      id: error.id,
      severity: error.severity,
      message: `${error.message} Rows ${rowList} are marked as ${error.relationshipLabel}.`,
    });
  });

  values.members.forEach((member, index) => {
    const name = getMemberDisplayName(member);
    const age = resolveMemberAge(member, ageReferenceDate).ageYears;
    const ageValidation = validateMemberAgeSource(member, ageReferenceDate);
    const relationship = cleanRBIMCode("q02_relationship_to_head", member.relationshipToHead);
    const relationshipLabel = relationship.label;
    const civilStatus = cleanRBIMCode("q08_marital_status", member.civilStatus);
    const relationshipSexWarning = checkRelationshipSexWarning(member.relationshipToHead, member.sex);
    const reasonNotAttending = cleanRBIMCode("q15_reason_not_attending_school", member.reasonNotAttendingSchool);

    if (!ageValidation.isValid) {
      notices.push({
        id: `member-${index}-invalid-birthdate`,
        severity: "error",
        message: `${name}: ${ageValidation.message}`,
      });
    }

    if (isReasonNotAttendingRequired(member) && !member.reasonNotAttendingSchool?.trim()) {
      notices.push({
        id: `member-${index}-missing-q15-reason-not-attending`,
        severity: "error",
        message: `Line ${index + 1}: Q15 Reason for Not Attending School is required when Q12 is No.`,
      });
    }

    if (
      isReasonNotAttendingRequired(member) &&
      !reasonNotAttending.error &&
      reasonNotAttending.code === "20" &&
      !hasOtherSpecify(member.reasonNotAttendingSchool, "20") &&
      !member.reasonNotAttendingSchoolOther?.trim()
    ) {
      notices.push({
        id: `member-${index}-missing-q15-other-specify`,
        severity: "error",
        message: `Line ${index + 1}: Please specify the reason for not attending school.`,
      });
    }

    if (relationshipSexWarning) {
      notices.push({
        id: `member-${index}-relationship-sex-warning`,
        severity: "warning",
        message: `Line ${index + 1}: ${relationshipSexWarning.message} Please review before submitting.`,
      });
    }

    getSkipPatternConflicts(member, index).forEach((notice) => notices.push(notice));
    getDatePatternErrors(member, index).forEach((notice) => notices.push(notice));

    const skills = cleanRBIMCode("q45_skills", member.skills);
    if (
      getApplicabilityForQuestion(member, "skills", age) === "applicable" &&
      !skills.error &&
      skills.code === "18" &&
      !hasOtherSpecify(member.skills, "18")
    ) {
      notices.push({
        id: `member-${index}-missing-q45-other-specify`,
        severity: "error",
        message: `Line ${index + 1}: Q45 Skills requires a specified response when Others is selected.`,
      });
    }

    if (relationshipLabel === "Head" && age !== null && age < 15) {
      notices.push({
        id: `member-${index}-young-head`,
        severity: "warning",
        message: `${name}: Head is below 15 years old.`,
      });
    }

    if (relationship.code === "02" && age !== null && age < 15) {
      notices.push({
        id: `member-${index}-young-spouse`,
        severity: "warning",
        message: `${name}: Spouse/partner is below 15 years old.`,
      });
    }

    if (!civilStatus.error && civilStatus.code === "1" && relationship.code === "02") {
      notices.push({
        id: `member-${index}-single-spouse`,
        severity: "warning",
        message: `${name}: Relationship is Spouse but civil status is Single.`,
      });
    }
  });

  getAddNewDataCodeErrors(values).forEach((message, index) => {
    notices.push({
      id: `invalid-rbim-code-${index}`,
      severity: "error",
      message,
    });
  });

  return notices;
}

export function validateHousehold(values: AddNewDataFormValues) {
  return getSuspiciousEntryNotices(values);
}

export function validateMember(member: AddNewDataFormValues["members"][number], lineNumber = 1) {
  const index = Math.max(lineNumber - 1, 0);
  const notices: ValidationNotice[] = [];
  const name = getMemberDisplayName(member);
  const relationshipSexWarning = checkRelationshipSexWarning(member.relationshipToHead, member.sex);
  const relationship = cleanRBIMCode("q02_relationship_to_head", member.relationshipToHead);
  const civilStatus = cleanRBIMCode("q08_marital_status", member.civilStatus);
  const reasonNotAttending = cleanRBIMCode("q15_reason_not_attending_school", member.reasonNotAttendingSchool);
  const age = resolveMemberAge(member).ageYears;
  const ageValidation = validateMemberAgeSource(member);

  if (!ageValidation.isValid) {
    notices.push({
      id: `member-${index}-invalid-birthdate`,
      severity: "error",
      message: `${name}: ${ageValidation.message}`,
    });
  }

  if (isReasonNotAttendingRequired(member) && !member.reasonNotAttendingSchool?.trim()) {
    notices.push({
      id: `member-${index}-missing-q15-reason-not-attending`,
      severity: "error",
      message: `Line ${index + 1}: Q15 Reason for Not Attending School is required when Q12 is No.`,
    });
  }

  if (
    isReasonNotAttendingRequired(member) &&
    !reasonNotAttending.error &&
    reasonNotAttending.code === "20" &&
    !hasOtherSpecify(member.reasonNotAttendingSchool, "20") &&
    !member.reasonNotAttendingSchoolOther?.trim()
  ) {
    notices.push({
      id: `member-${index}-missing-q15-other-specify`,
      severity: "error",
      message: `Line ${index + 1}: Please specify the reason for not attending school.`,
    });
  }

  if (relationshipSexWarning) {
    notices.push({
      id: `member-${index}-relationship-sex-warning`,
      severity: "warning",
      message: `Line ${index + 1}: ${relationshipSexWarning.message} Please review before submitting.`,
    });
  }

  if (relationship.code === "02" && age !== null && age < 15) {
    notices.push({
      id: `member-${index}-young-spouse`,
      severity: "warning",
      message: `${name}: Spouse/partner is below 15 years old.`,
    });
  }

  if (!civilStatus.error && civilStatus.code === "1" && relationship.code === "02") {
    notices.push({
      id: `member-${index}-single-spouse`,
      severity: "warning",
      message: `${name}: Relationship is Spouse but civil status is Single.`,
    });
  }

  getSkipPatternConflicts(member, index).forEach((notice) => notices.push(notice));
  getDatePatternErrors(member, index).forEach((notice) => notices.push(notice));

  const skills = cleanRBIMCode("q45_skills", member.skills);
  if (
    getApplicabilityForQuestion(member, "skills", age) === "applicable" &&
    !skills.error &&
    skills.code === "18" &&
    !hasOtherSpecify(member.skills, "18")
  ) {
    notices.push({
      id: `member-${index}-missing-q45-other-specify`,
      severity: "error",
      message: `Line ${index + 1}: Q45 Skills requires a specified response when Others is selected.`,
    });
  }

  return notices;
}

const skipPatternFieldLabels: Partial<Record<keyof AddNewDataFormValues["members"][number], string>> = {
  highestEducation: "Q11 Highest Level of Education Completed",
  currentlyEnrolled: "Q12 Currently Enrolled",
  schoolLevel: "Q13 School Level",
  placeOfSchool: "Q14 Place of School",
  reasonNotAttendingSchool: "Q15 Reason for Not Attending School",
  monthlyIncome: "Q16 Monthly Income",
  sourceOfIncome: "Q17 Source of Income",
  workStatus: "Q18 Status of Work/Business",
  placeOfWork: "Q19 Place of Work/Business",
  placeOfDelivery: "Q20 Place of Delivery",
  birthAttendant: "Q21 Birth Attendant",
  immunization: "Q22 Immunization",
  livingChildren: "Q23 Living Children",
  familyPlanningUse: "Q24 Family Planning Use",
  fpMethodSource: "Q25 Source of FP Method",
  intentionToUseFp: "Q26 Intention to Use FP",
  soloParentStatus: "Q31 Solo Parent",
  registeredSeniorCitizen: "Q32 Registered Senior Citizen",
  registeredVoterBarangay: "Q33 Registered Voter",
  lengthOfStayInBarangay: "Q34 Length of Stay in Barangay",
  previousResidenceFiveYears: "Q35 Previous Residence 5 Years Ago",
  previousResidenceSixMonths: "Q36 Previous Residence 6 Months Ago",
  residentType: "Q37 Type of Resident",
  dateOfTransfer: "Q38 Date of Transfer",
  reasonLeavingPreviousResidence1: "Q39A Reason for Leaving Previous Residence",
  reasonLeavingPreviousResidence2: "Q39B Reason for Leaving Previous Residence",
  reasonLeavingPreviousResidence3: "Q39C Reason for Leaving Previous Residence",
  returnToPreviousResidence: "Q40 Return to Previous Residence",
  transferReason1: "Q41A Reason for Transferring to Barangay",
  transferReason2: "Q41B Reason for Transferring to Barangay",
  transferReason3: "Q41C Reason for Transferring to Barangay",
  durationOfStayCurrentBarangay: "Q42 Duration/Intent to Stay in Current Barangay",
  hasValidCtc: "Q43A Has Valid CTC",
  ctcIssuedInBarangay: "Q43B CTC Issued in this Barangay",
  skillsTrainingInterest: "Q44 Skills Development Training",
  skills: "Q45 Skills",
};

function getSkipPatternConflicts(
  member: AddNewDataFormValues["members"][number],
  memberIndex: number,
): ValidationNotice[] {
  void member;
  void memberIndex;

  return [];
}

function getRequiredFieldMessage(field: MemberFieldName, fallbackLabel: string) {
  if (field === "placeOfBirth") {
    return "Place of Birth is required.";
  }

  if (field === "placeOfSchool") {
    return "Please write the place of school.";
  }

  return `${fallbackLabel} is required before proceeding.`;
}

function getCodeErrorMessage(field: MemberFieldName, fallbackMessage: string) {
  if (field === "sex") {
    return "Please select or enter a valid sex.";
  }

  return fallbackMessage;
}

function getDatePatternErrors(
  member: AddNewDataFormValues["members"][number],
  memberIndex: number,
): ValidationNotice[] {
  if (
    getApplicabilityForQuestion(member, "dateOfTransfer") !== "applicable" ||
    !isMeaningfulSkippedAnswer(member.dateOfTransfer)
  ) {
    return [];
  }

  if (isValidMonthYear(member.dateOfTransfer)) {
    return [];
  }

  return [{
    id: `member-${memberIndex}-q38-invalid-month-year`,
    severity: "error",
    message: `Line ${memberIndex + 1}: Q38 Date of Transfer must use a valid MM/YYYY format.`,
  }];
}

function isMeaningfulSkippedAnswer(value: string | undefined) {
  const normalizedValue = String(value ?? "").trim();

  if (!normalizedValue || /^none$/i.test(normalizedValue)) {
    return false;
  }

  return !isRBIMNotApplicable(normalizedValue);
}

type MonthYearValidationOptions = {
  allowFuture?: boolean;
  requireTwoDigitMonth?: boolean;
};

function isValidMonthYear(
  value: string | undefined,
  options: MonthYearValidationOptions = {},
) {
  const monthPattern = options.requireTwoDigitMonth ? "\\d{2}" : "\\d{1,2}";
  const match = new RegExp(`^(${monthPattern})\\/(\\d{4})$`).exec(String(value ?? "").trim());

  if (!match) {
    return false;
  }

  const month = Number(match[1]);
  const year = Number(match[2]);

  if (month < 1 || month > 12 || year < 1900) {
    return false;
  }

  if (options.allowFuture) {
    return true;
  }

  const today = new Date();
  const currentMonth = today.getMonth() + 1;
  const currentYear = today.getFullYear();

  return year < currentYear || (year === currentYear && month <= currentMonth);
}

function isReasonNotAttendingRequired(member: AddNewDataFormValues["members"][number]) {
  return getApplicabilityForQuestion(member, "reasonNotAttendingSchool") === "applicable";
}

function hasOtherSpecify(value: string | undefined, code: string) {
  return new RegExp(`^0*${code}(?:\\s*(?:-|:|,)\\s*|\\s+)\\S+`, "i").test(String(value ?? "").trim());
}

function getRowsByRelationshipCode(members: AddNewDataFormValues["members"], code: "01" | "02") {
  return members
    .map((member, index) => {
      const result = cleanRBIMCode("q02_relationship_to_head", member.relationshipToHead);

      return !result.error && result.code === code ? index + 1 : -1;
    })
    .filter((row) => row >= 1);
}

function formatRows(rows: number[]) {
  if (rows.length <= 1) {
    return rows.join("");
  }

  if (rows.length === 2) {
    return `${rows[0]} and ${rows[1]}`;
  }

  return `${rows.slice(0, -1).join(", ")}, and ${rows[rows.length - 1]}`;
}
